import React, { useEffect, useState } from "react";
import { Box, Button, FormControl, FormLabel, HStack, Input, Select, Text, VStack, Wrap, WrapItem, useToast } from "@chakra-ui/react";
import VmsPageCard from "../components/VmsPageCard";
import { getVmsUser, isVmsGeneralAdmin, isVmsOfficeUser } from "../utils/auth";
import { vmsApi } from "../services/vmsApi";

const VmsBookingLinks = () => {
  const toast = useToast();
  const user = getVmsUser();
  const isAdmin = isVmsGeneralAdmin();
  const isOfficeUser = isVmsOfficeUser();
  const myPersonId = String(user?.person_id || user?.office_person_id || user?.person?.person_id || "");
  const [offices, setOffices] = useState([]);
  const [persons, setPersons] = useState([]);
  const [form, setForm] = useState({ office_id: "", person_id: "" });
  const [bookingLink, setBookingLink] = useState("");
  const [bookingNote, setBookingNote] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isPersonsLoading, setIsPersonsLoading] = useState(false);

  useEffect(() => {
    const init = async () => {
      if (isAdmin) {
        try {
          const data = await vmsApi.getOffices();
          setOffices(Array.isArray(data) ? data : []);
        } catch {
          setOffices([]);
        }
      } else {
        const officeId = String(user?.office_id || user?.office?.office_id || user?.office?.id || "");
        if (officeId) {
          setForm((prev) => ({ ...prev, office_id: officeId, person_id: myPersonId }));
        }
      }
    };
    init().catch(() => {});
  }, [isAdmin, user, myPersonId]);

  useEffect(() => {
    const officeId = String(form.office_id || "");
    if (!officeId) {
      setPersons([]);
      return;
    }
    const loadPersons = async () => {
      setIsPersonsLoading(true);
      try {
        const data = await vmsApi.getPublicOfficePersons(officeId);
        const list = Array.isArray(data?.persons) ? data.persons : Array.isArray(data) ? data : [];
        setPersons(list);
      } catch {
        setPersons([]);
      } finally {
        setIsPersonsLoading(false);
      }
    };
    loadPersons().catch(() => {});
  }, [form.office_id]);

  const generate = async () => {
    setIsLoading(true);
    try {
      const officeId = isAdmin ? form.office_id : String(user?.office_id || user?.office?.office_id || "");
      const officeUserPersonId = !isAdmin ? myPersonId : "";
      if (isOfficeUser && !officeUserPersonId) {
        toast({
          title: "Profile not linked",
          description: "Your account is not linked to a host profile yet. Contact admin to link person email to this login.",
          status: "warning",
        });
        return;
      }
      const res = await vmsApi.getOfficeBookingLink({
        ...(officeId ? { office_id: officeId } : {}),
        ...(isAdmin ? (form.person_id ? { person_id: form.person_id } : {}) : { person_id: officeUserPersonId }),
      });
      setBookingLink(res?.booking_url || "");
      setBookingNote(res?.configuration_note || "");
      if (!res?.booking_url) {
        toast({
          title: "Booking URL unavailable",
          description: res?.configuration_note || "Backend did not return a URL.",
          status: "warning",
        });
      }
    } catch (err) {
      toast({ title: "Failed to generate link", description: err?.message, status: "error" });
    } finally {
      setIsLoading(false);
    }
  };

  const copyLink = async () => {
    if (!bookingLink) return;
    try {
      await navigator.clipboard.writeText(bookingLink);
      toast({ title: "Booking link copied", status: "success" });
    } catch {
      toast({ title: "Copy failed", description: "Copy manually from the field.", status: "warning" });
    }
  };

  return (
    <VStack align="stretch" spacing={5}>
      <VmsPageCard title="Booking Links" description="Generate and share visitor booking links by office and host.">
        <Wrap spacing={4} align="end">
          {isAdmin ? (
            <WrapItem minW={{ base: "100%", md: "280px" }}>
              <FormControl isRequired>
                <FormLabel mb={1}>Office</FormLabel>
                <Select
                  value={form.office_id}
                  onChange={(e) => setForm((prev) => ({ ...prev, office_id: e.target.value, person_id: "" }))}
                >
                  <option value="">Select office</option>
                  {offices.map((office) => {
                    const id = String(office.id || office.office_id || "");
                    return (
                      <option key={id} value={id}>
                        {office.name || office.code || id}
                      </option>
                    );
                  })}
                </Select>
              </FormControl>
            </WrapItem>
          ) : null}
          {isAdmin ? (
            <WrapItem minW={{ base: "100%", md: "320px" }}>
              <FormControl>
                <FormLabel mb={1}>Host (Optional)</FormLabel>
                <Select
                  value={form.person_id}
                  onChange={(e) => setForm((prev) => ({ ...prev, person_id: e.target.value }))}
                  isDisabled={!form.office_id || isPersonsLoading}
                >
                  <option value="">Office booking only</option>
                  {persons.map((person) => {
                    const id = String(person?.person_id || person?.id || "");
                    return (
                      <option key={id} value={id}>
                        {person?.full_name || person?.name || id}
                      </option>
                    );
                  })}
                </Select>
              </FormControl>
            </WrapItem>
          ) : null}
          <WrapItem>
            <Button colorScheme="green" onClick={generate} isLoading={isLoading}>
              Generate Link
            </Button>
          </WrapItem>
        </Wrap>

        <HStack mt={4} spacing={3} align="stretch">
          <Input value={bookingLink} isReadOnly placeholder="Booking URL will appear here" bg="gray.50" />
          <Button variant="outline" onClick={copyLink} isDisabled={!bookingLink}>
            Copy Link
          </Button>
        </HStack>
        {bookingNote ? (
          <Text mt={2} fontSize="sm" color="orange.700">
            {bookingNote}
          </Text>
        ) : null}
      </VmsPageCard>
    </VStack>
  );
};

export default VmsBookingLinks;
